import { useCallback, useEffect, useRef, useState } from 'react'
import { useApolloClient } from '@apollo/client/react'
import { MAX_FILES_PER_MESSAGE } from './chat-file-format'
import {
  FileProcessingError,
  removeUploadedFile,
  uploadAndProcess,
} from './chat-file-upload'

const UNSAVED = 'unsaved'

type ComposerFileStatus = 'uploading' | 'processing' | 'ready' | 'failed'

export type ComposerFile = {
  key: string
  name: string
  size: number
  status: ComposerFileStatus
  progress: number
  fileId?: string
  failureCode?: string
}

type FilesByOwner = Partial<Record<string, Array<ComposerFile>>>

function isAbort(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError'
}

function useFileState(key: string) {
  const [state, setState] = useState<FilesByOwner>({})
  const patch = useCallback(
    (owner: string, fileKey: string, change: Partial<ComposerFile>) =>
      setState((current) => ({
        ...current,
        [owner]: (current[owner] ?? []).map((item) =>
          item.key === fileKey ? { ...item, ...change } : item,
        ),
      })),
    [],
  )
  const drop = useCallback(
    (owner: string, keys: Array<string>) =>
      setState((current) => ({
        ...current,
        [owner]: (current[owner] ?? []).filter(
          (item) => !keys.includes(item.key),
        ),
      })),
    [],
  )
  const moveToSession = useCallback((next: string) => {
    setState((current) => {
      const unsaved = current[UNSAVED]
      if (!unsaved?.length) return current
      const { [UNSAVED]: _moved, ...rest } = current

      return { ...rest, [next]: [...(rest[next] ?? []), ...unsaved] }
    })
  }, [])

  return { files: state[key] ?? [], setState, patch, drop, moveToSession }
}

function useUploadRunner(
  ensureSession: () => Promise<string>,
  patch: ReturnType<typeof useFileState>['patch'],
) {
  const client = useApolloClient()
  const sources = useRef(new Map<string, File>())
  const controllers = useRef(new Map<string, AbortController>())
  useEffect(() => {
    const active = controllers.current

    return () => {
      for (const controller of active.values()) controller.abort()
      active.clear()
    }
  }, [])

  const run = useCallback(
    async (owner: string, fileKey: string) => {
      const source = sources.current.get(fileKey)
      if (!source) return
      const controller = new AbortController()
      controllers.current.set(fileKey, controller)
      patch(owner, fileKey, {
        status: 'uploading',
        progress: 0,
        failureCode: undefined,
      })
      try {
        const fileId = await uploadAndProcess({
          client,
          ensureSession,
          source,
          signal: controller.signal,
          onProgress: (progress) => patch(owner, fileKey, { progress }),
          onUploaded: (id) =>
            patch(owner, fileKey, { fileId: id, status: 'processing' }),
        })
        patch(owner, fileKey, { status: 'ready', fileId, progress: 100 })
      } catch (error) {
        if (isAbort(error)) return
        patch(owner, fileKey, {
          status: 'failed',
          failureCode:
            error instanceof FileProcessingError
              ? error.failureCode
              : 'upload_failed',
        })
      } finally {
        controllers.current.delete(fileKey)
      }
    },
    [client, ensureSession, patch],
  )

  return {
    run,
    sources,
    cancel: (fileKey: string) => controllers.current.get(fileKey)?.abort(),
    discard: (fileId?: string) => removeUploadedFile(client, fileId),
  }
}

/**
 * Composer file state per chat session: upload, scan/processing status,
 * retry and removal. The chat session is created on the first added file so
 * uploads can be validated against the session's workspace.
 */
export function useAiChatFiles({
  sessionKey,
  ensureSession,
}: {
  sessionKey?: string
  ensureSession: () => Promise<string>
}) {
  const key = sessionKey ?? UNSAVED
  const state = useFileState(key)
  const runner = useUploadRunner(ensureSession, state.patch)
  const counter = useRef(0)
  const [notice, setNotice] = useState<'tooMany' | null>(null)
  const { files } = state

  const add = (incoming: Array<File>) => {
    const accepted = incoming.slice(
      0,
      Math.max(0, MAX_FILES_PER_MESSAGE - files.length),
    )
    const created = accepted.map((file): ComposerFile => {
      counter.current += 1
      const fileKey = `local-${counter.current}`
      runner.sources.current.set(fileKey, file)

      return {
        key: fileKey,
        name: file.name,
        size: file.size,
        status: 'uploading',
        progress: 0,
      }
    })
    setNotice(incoming.length > accepted.length ? 'tooMany' : null)
    state.setState((current) => ({
      ...current,
      [key]: [...(current[key] ?? []), ...created],
    }))
    for (const item of created) void runner.run(key, item.key)
  }

  const remove = (fileKey: string) => {
    const item = files.find((value) => value.key === fileKey)
    runner.cancel(fileKey)
    runner.sources.current.delete(fileKey)
    setNotice(null)
    state.drop(key, [fileKey])
    runner.discard(item?.fileId)
  }

  const retry = (fileKey: string) => {
    runner.discard(files.find((item) => item.key === fileKey)?.fileId)
    void runner.run(key, fileKey)
  }

  const settle = (owner: string, sentKeys: Array<string>) => {
    for (const fileKey of sentKeys) runner.sources.current.delete(fileKey)
    state.drop(owner, sentKeys)
  }

  const ready = files.filter((item) => item.status === 'ready' && item.fileId)

  return {
    files,
    add,
    retry,
    remove,
    settle,
    moveToSession: state.moveToSession,
    readyIds: ready.map((item) => item.fileId as string),
    readyKeys: ready.map((item) => item.key),
    busy: files.some(
      (item) => item.status === 'uploading' || item.status === 'processing',
    ),
    failed: files.some((item) => item.status === 'failed'),
    notice,
    sessionKey: key,
  }
}
